import { Link } from 'react-router';
import { RadioTower, Settings, Keyboard, CircleQuestionMark } from 'lucide-react';
import type { LiveSummary } from '@multimod/shared';
import { StreamerPill } from './StreamerPill.js';
import { ConnCluster } from './ConnCluster.js';
import { CmdkTrigger } from './CmdkTrigger.js';
import { FeedbackButton } from './FeedbackButton.js';
import { DevMockBadge } from '@/features/dev-mock/DevMockBadge';
import type { ChatFeedStore } from '@/features/chat-feed/store';

interface Props {
  selected: LiveSummary | null;
  twitch: LiveSummary | null;
  youtube: LiveSummary | null;
  // Store ativo (quando há live) — alimenta o estado de conexão por plataforma
  // nos chips do ConnCluster. Null quando nenhuma live está conectada.
  store?: ChatFeedStore | null;
  onOpenDiscovery: () => void;
  onOpenShortcuts: () => void;
  onOpenPalette: () => void;
  onLogout: () => void;
}

function IconBtn({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const placeholder = !onClick;
  return (
    <button
      type="button"
      aria-label={placeholder ? `${label} (em breve)` : label}
      title={placeholder ? 'Em breve' : label}
      onClick={onClick}
      className="w-8 h-8 grid place-items-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors duration-fast focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {children}
    </button>
  );
}

export function TopBar({ selected, twitch, youtube, store = null, onOpenDiscovery, onOpenShortcuts, onOpenPalette, onLogout }: Props) {
  return (
    <header
      role="banner"
      className="flex items-center gap-3 h-14 px-4 bg-bg-elevated border-b border-border"
    >
      <Link to="/painel" className="flex items-center gap-2 pr-1 font-semibold text-base tracking-tight">
        <span
          className="w-[22px] h-[22px] rounded-[5px] grid place-items-center text-primary-fg"
          style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(192 76% 42%))' }}
        >
          <RadioTower size={13} strokeWidth={2.5} />
        </span>
        Multimod
      </Link>

      {/* #113: separador vertical (.sep-v do prototype = w-px bg-border) marca a
          quebra brand → contexto da live. */}
      <div className="w-px h-6 bg-border shrink-0" aria-hidden="true" />

      <DevMockBadge />

      <StreamerPill selected={selected} onConnect={onOpenDiscovery} onLogout={onLogout} />

      <ConnCluster
        twitch={twitch ? { liveChatId: twitch.liveChatId, viewerCount: twitch.viewerCount } : null}
        youtube={youtube ? { liveChatId: youtube.liveChatId, viewerCount: youtube.viewerCount } : null}
        store={store}
      />

      <div className="ml-auto flex items-center gap-2">
        {/* #113: separa o cluster de live do cluster de ferramentas (busca/ajuda). */}
        <div className="w-px h-6 bg-border shrink-0 mr-1" aria-hidden="true" />
        <CmdkTrigger onClick={onOpenPalette} />
        <FeedbackButton />
        <Link
          to="/settings"
          aria-label="Configurações"
          title="Configurações"
          className="w-8 h-8 grid place-items-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors duration-fast focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Settings size={16} strokeWidth={1.75} />
        </Link>
        <IconBtn label="Atalhos" onClick={onOpenShortcuts}><Keyboard size={16} strokeWidth={1.75} /></IconBtn>
        <IconBtn label="Ajuda"><CircleQuestionMark size={16} strokeWidth={1.75} /></IconBtn>
      </div>
    </header>
  );
}
